import { PATHS } from 'router/config';

import { IMarketingCompareRequestInput } from './marketingApi';
import { marketingLegacyTabTarget } from './marketingLegacyLinks';
import {
  IMarketingSpendView,
  isMarketingChannel,
  marketingChannelFromKey,
  marketingIsHypothesis,
  marketingNumber,
  marketingShiftDay,
  marketingSpendView,
  MARKETING_NONE_KEY,
} from './marketingModel';
import {
  IMarketingAction,
  IMarketingBreakdownRow,
  IMarketingSeriesPoint,
  IMarketingSignal,
  MarketingDate,
} from './types';

/**
 * ТЗ 0103 — «Сегодня» (design-erp §3.1): что чинить или усиливать сейчас.
 * Чистый модуль: секции действий, приоритет видов, ранжирование и полоса
 * «Коротко» из ряда по дням.
 *
 * Этап 2: действия отдаёт сервер (`marketingActions`) уже по рангу
 * (важность → приоритет вида → оценка в PLN → свежесть); фронт раскладывает
 * их по секциям и оставляет одну карточку на сущность. Тексты — те же, что
 * у ленты сигналов (`marketingSignalTitle` / `marketingSignalEvidence`):
 * действие приводится к форме сигнала (`marketingActionSignal`).
 */

/** Секции «Сегодня» — в порядке экрана. */
export const MARKETING_ACTION_SECTIONS = ['waste', 'grow', 'data'] as const;

export type MarketingActionSection = typeof MARKETING_ACTION_SECTIONS[number];

/**
 * Секция вида сигнала. Здесь и виды этапа 2 (`leads_no_sales`,
 * `sent_not_signed`, SEO, страницы) — сервер начнёт их слать без правки
 * фронта. Гипотезы по бюджету — вторая секция целиком (блок «Гипотезы»).
 */
export const MARKETING_ACTION_SECTION_OF: Readonly<
  Record<string, MarketingActionSection>
> = {
  spend_no_leads: 'waste',
  cpl_spike: 'waste',
  creative_fatigue: 'waste',
  leads_no_sales: 'waste',
  sent_not_signed: 'waste',
  budget_hypothesis: 'grow',
  demand_unserved: 'grow',
  demand_rising: 'grow',
  seo_position_drop: 'grow',
  seo_gap_new: 'grow',
  ai_visibility_drop: 'grow',
  page_conversion_drop: 'grow',
  tracking_gap: 'data',
  campaign_mapping_gap: 'data',
  coverage_drop: 'data',
  source_stale: 'data',
  offline_retract_hold: 'data',
  seo_budget_hold: 'data',
  // ТЗ 0103 §E6: падение видимости и рост конкурента — что усиливать в
  // SEO; возможности SEO (`source: seo`) — тоже «Стоит усилить».
  serp_rank_drop: 'grow',
  competitor_gain: 'grow',
  gap_keyword: 'grow',
  missing_family: 'grow',
  content_topic: 'grow',
  striking_distance: 'grow',
  click_decline: 'grow',
  ai_absent: 'grow',
  aio_not_cited: 'grow',
  link_gap: 'grow',
};

/**
 * Приоритет вида внутри одной важности: меньше — выше. Деньги, которые
 * уходят прямо сейчас, — раньше того, что можно усилить; починка данных —
 * по тяжести последствий (без меток врут все цифры, устаревший источник —
 * только свежие).
 */
export const MARKETING_ACTION_PRIORITY: Readonly<Record<string, number>> = {
  spend_no_leads: 10,
  cpl_spike: 20,
  leads_no_sales: 30,
  sent_not_signed: 40,
  creative_fatigue: 50,
  budget_hypothesis: 10,
  demand_unserved: 20,
  seo_gap_new: 30,
  demand_rising: 40,
  page_conversion_drop: 50,
  seo_position_drop: 60,
  ai_visibility_drop: 70,
  tracking_gap: 10,
  campaign_mapping_gap: 20,
  coverage_drop: 30,
  offline_retract_hold: 40,
  source_stale: 50,
  seo_budget_hold: 60,
  serp_rank_drop: 55,
  competitor_gain: 65,
  // Возможности SEO — после сигналов: их много, и сервер сам ранжирует их
  // оценкой в PLN.
  striking_distance: 80,
  gap_keyword: 81,
  missing_family: 82,
  // Тема для статьи или страницы — после темы для нового курса (как у
  // сервера: content_topic 8 против missing_family 7).
  content_topic: 82.5,
  click_decline: 83,
  aio_not_cited: 84,
  ai_absent: 85,
  link_gap: 86,
};

/** Карточек в секции до «Показать ещё N». */
export const MARKETING_ACTION_SECTION_LIMIT = 5;

const SEVERITY_RANK: Readonly<Record<string, number>> = {
  critical: 0,
  warning: 1,
  info: 2,
};

/** Секция сигнала; незнакомый вид — «Данные требуют внимания» не врёт. */
export const marketingActionSection = (
  signal: Pick<IMarketingSignal, 'kind'>,
): MarketingActionSection => MARKETING_ACTION_SECTION_OF[signal.kind] ?? 'data';

/**
 * Ценность сигнала для сортировки внутри вида: `score` сервера, иначе
 * деньги на кону (расход без лидов, ценность гипотезы).
 */
const valueOf = (signal: IMarketingSignal): number => {
  const metrics = signal.metrics ?? {};
  const raw =
    metrics.score ??
    metrics.value_pln ??
    metrics.spend_pln ??
    metrics.spend ??
    null;
  const value =
    typeof raw === 'number' || typeof raw === 'string'
      ? marketingNumber(raw)
      : null;
  return value ?? 0;
};

/** Ключ сущности: одна карточка на объявление / кампанию / курс. */
const entityKey = (signal: IMarketingSignal): string => {
  if (signal.entityId) return `entity:${signal.entityId}`;
  if (signal.familySlug) return `family:${signal.familySlug}`;
  return `signal:${signal.id}`;
};

/** Сравнение двух сигналов: важность → приоритет вида → ценность → свежесть. */
export const marketingActionCompare = (
  a: IMarketingSignal,
  b: IMarketingSignal,
): number => {
  const severity =
    (SEVERITY_RANK[a.severity] ?? 3) - (SEVERITY_RANK[b.severity] ?? 3);
  if (severity) return severity;
  const priority =
    (MARKETING_ACTION_PRIORITY[a.kind] ?? 99) -
    (MARKETING_ACTION_PRIORITY[b.kind] ?? 99);
  if (priority) return priority;
  const value = valueOf(b) - valueOf(a);
  if (value) return value;
  return (b.lastSeen || '').localeCompare(a.lastSeen || '');
};

export type IMarketingActionSections = Readonly<
  Record<MarketingActionSection, IMarketingSignal[]>
>;

/**
 * Сигналы → секции «Сегодня», по рангу; одна карточка на сущность (самая
 * важная), гипотезы — отдельно (у них свой блок со своей уверенностью).
 */
export const marketingRankActions = (
  signals: ReadonlyArray<IMarketingSignal>,
): IMarketingActionSections => {
  const sections: Record<MarketingActionSection, IMarketingSignal[]> = {
    waste: [],
    grow: [],
    data: [],
  };
  const seen = new Set<string>();
  [...signals]
    .filter((signal) => !marketingIsHypothesis(signal))
    .sort(marketingActionCompare)
    .forEach((signal) => {
      const key = entityKey(signal);
      if (seen.has(key)) return;
      seen.add(key);
      sections[marketingActionSection(signal)].push(signal);
    });
  return sections;
};

/* ── «Коротко» ───────────────────────────────────────────────────────── */

/** Дней ряда полосы: 7 дней и 7 до них. */
export const MARKETING_TODAY_DAYS = 14;

export interface IMarketingTodayStrip {
  /** Лиды вчера и в тот же день неделю назад. */
  leadsYesterday: number | null;
  leadsWeekAgo: number | null;
  /**
   * Лиды за 7 ПОЛНЫХ дней по вчера и за 7 до них: неполный сегодняшний день
   * против 7 полных занижал бы неделю (повторное ревью 0103, A10).
   */
  leads7: number | null;
  leadsPrev7: number | null;
  sales7: number | null;
  salesPrev7: number | null;
  /** Расход вчера: полный, за собранные дни или неизвестен. */
  spendYesterday: IMarketingSpendView;
  /** Ряды 14 дней для спарклайнов (`null` — дня нет в ответе). */
  leads14: Array<number | null>;
  sales14: Array<number | null>;
  spend14: Array<number | null>;
}

/** Плитка «Коротко» — у каждой своя вкладка за то же окно. */
export const MARKETING_TODAY_TILES = [
  'leadsYesterday',
  'leads7',
  'sales7',
  'contracts',
  'spendYesterday',
] as const;

export type MarketingTodayTile = typeof MARKETING_TODAY_TILES[number];

/** Окно «7 дней» «Сегодня»: 7 полных дней по вчера (A10). */
export const marketingTodayWeek = (
  today: MarketingDate,
): { from: MarketingDate; to: MarketingDate } => ({
  from: marketingShiftDay(today, -7),
  to: marketingShiftDay(today, -1),
});

/**
 * Сравнение плитки на целевом экране: «прошлый период» (7 дней против 7
 * до них — окно той же длины прямо перед), свой день (вчера против того же
 * дня неделю назад) или `null` — как в текущем адресе.
 */
export type MarketingTodayTileCompare =
  | { mode: 'prev' }
  | { mode: 'custom'; from: MarketingDate; to: MarketingDate }
  | null;

export interface IMarketingTodayTileTarget {
  tab: 'leads' | 'ads';
  /** Окно целевого экрана — ровно то, что посчитала плитка. */
  from: MarketingDate;
  to: MarketingDate;
  compare: MarketingTodayTileCompare;
  leadMetric: 'crmLeads' | 'sales' | 'contractsSent' | 'spendPln';
}

/**
 * Куда ведёт плитка: вкладка, окно, сравнение и метрика графика — те же,
 * что у самой плитки (повторное ревью 0103, A10): «Лиды вчера» — вчера
 * против того же дня неделю назад (свои даты сравнения), «7 дней» — 7
 * полных дней по вчера против 7 до них; расход вчера — «Реклама» без
 * своего сравнения.
 */
export const marketingTodayTileTarget = (
  tile: MarketingTodayTile,
  today: MarketingDate,
): IMarketingTodayTileTarget => {
  const yesterday = marketingShiftDay(today, -1);
  const weekAgo = marketingShiftDay(yesterday, -7);
  const week = marketingTodayWeek(today);
  const weekTarget = (
    leadMetric: IMarketingTodayTileTarget['leadMetric'],
  ): IMarketingTodayTileTarget => ({
    tab: 'leads',
    ...week,
    compare: { mode: 'prev' },
    leadMetric,
  });
  switch (tile) {
    case 'leadsYesterday':
      return {
        tab: 'leads',
        from: yesterday,
        to: yesterday,
        compare: { mode: 'custom', from: weekAgo, to: weekAgo },
        leadMetric: 'crmLeads',
      };
    case 'sales7':
      return weekTarget('sales');
    case 'contracts':
      return weekTarget('contractsSent');
    case 'spendYesterday':
      return {
        tab: 'ads',
        from: yesterday,
        to: yesterday,
        compare: null,
        leadMetric: 'spendPln',
      };
    case 'leads7':
    default:
      return weekTarget('crmLeads');
  }
};

/** Окно ряда полосы: 14 полных дней по вчера (A10). */
export const marketingTodayWindow = (
  today: MarketingDate,
): { from: MarketingDate; to: MarketingDate } => ({
  from: marketingShiftDay(today, -MARKETING_TODAY_DAYS),
  to: marketingShiftDay(today, -1),
});

/**
 * Сравнение «7 дней против 7 до них» — «прошлый период» с единицей
 * `ROLLING` (ревью 0103 L1): окно той же длины ПРЯМО ПЕРЕД при любом дне
 * месяца — 1–7 октября сравниваются с 24–30 сентября, а не с 1–7 сентября.
 * Окно считает сервер (`compareInfo`).
 */
export const MARKETING_TODAY_COMPARE: Readonly<IMarketingCompareRequestInput> = {
  mode: 'prev',
  unit: 'ROLLING',
};

/**
 * Полоса «Коротко» из ряда по дням за 14 дней: вчера против того же дня
 * неделю назад, 7 дней против 7 до них. Сумма по дням, которых нет в ответе,
 * не считается (`null`), — ноль был бы враньём о несобранном.
 */
export const marketingTodayStrip = (
  series: ReadonlyArray<IMarketingSeriesPoint>,
  today: MarketingDate,
): IMarketingTodayStrip => {
  const byDay = new Map(series.map((point) => [point.period, point] as const));
  // Ряд — по вчера: сегодняшний день ещё идёт (A10).
  const { from } = marketingTodayWindow(today);
  const days = Array.from({ length: MARKETING_TODAY_DAYS }, (_, index) =>
    marketingShiftDay(from, index),
  );
  const values = (pick: (point: IMarketingSeriesPoint) => number | null) =>
    days.map((dayIso) => {
      const point = byDay.get(dayIso);
      return point ? pick(point) : null;
    });
  const leads14 = values((point) => point.metrics.crmLeads);
  const sales14 = values((point) => marketingNumber(point.metrics.sales));
  const spend14 = values((point) => marketingSpendView(point.metrics).value);
  const sum = (list: Array<number | null>) =>
    list.some((value) => value === null)
      ? null
      : list.reduce<number>((acc, value) => acc + (value ?? 0), 0);
  const yesterday = marketingShiftDay(today, -1);
  const weekAgo = marketingShiftDay(yesterday, -7);
  const at = (dayIso: string, list: Array<number | null>) =>
    list[days.indexOf(dayIso)] ?? null;
  return {
    leadsYesterday: at(yesterday, leads14),
    leadsWeekAgo: at(weekAgo, leads14),
    leads7: sum(leads14.slice(7)),
    leadsPrev7: sum(leads14.slice(0, 7)),
    sales7: sum(sales14.slice(7)),
    salesPrev7: sum(sales14.slice(0, 7)),
    spendYesterday: marketingSpendView(byDay.get(yesterday)?.metrics),
    leads14,
    sales14,
    spend14,
  };
};

/* ── Этап 2: действия сервера (`marketingActions`) ───────────────────── */

/**
 * Действие сервера → форма сигнала экрана: заголовок, доказательства и
 * ссылка строятся теми же помощниками, что у ленты. Канал сервера — код
 * базы (`meta`), экран — код enum (`META`); id — сигнала (для «Принято /
 * Скрыть»), без него — id действия.
 */
export const marketingActionSignal = (
  action: IMarketingAction,
): IMarketingSignal => {
  const channel = action.channel
    ? marketingChannelFromKey(action.channel)
    : null;
  return {
    id: action.signalId ?? action.id,
    kind: action.kind,
    severity: action.severity,
    status: 'open',
    entityId: action.entityId ?? null,
    entityName: action.entityName ?? null,
    familySlug: action.familySlug ?? null,
    channel: channel && isMarketingChannel(channel) ? channel : null,
    windowFrom: action.windowFrom ?? '',
    windowTo: action.windowTo ?? '',
    metrics: action.metrics,
    params: action.params,
    firstSeen: action.lastSeen ?? '',
    lastSeen: action.lastSeen ?? '',
    ackBy: null,
  };
};

/** Секция действия: гипотеза бюджета — «Стоит усилить» целиком. */
export const marketingActionSectionOf = (
  action: Pick<IMarketingAction, 'kind'>,
): MarketingActionSection => MARKETING_ACTION_SECTION_OF[action.kind] ?? 'data';

/** Ключ сущности действия: одна карточка на объявление / кампанию / курс. */
const actionEntityKey = (action: IMarketingAction): string => {
  if (action.inspector) return action.inspector;
  if (action.entityId) return `entity:${action.entityId}`;
  if (action.familySlug) return `family:${action.familySlug}`;
  return `action:${action.id}`;
};

export type IMarketingActionSectionsOf = Readonly<
  Record<MarketingActionSection, IMarketingAction[]>
>;

/**
 * Действия сервера → секции «Сегодня» В ПОРЯДКЕ СЕРВЕРА (он уже ранжировал);
 * в секции одна карточка на сущность — первая, то есть самая важная. Между
 * секциями сущность может повториться: «починить разметку площадки» (стоит
 * усилить) и «потеря меток той же площадки» (данные) — разные решения.
 */
export const marketingActionSections = (
  actions: ReadonlyArray<IMarketingAction>,
): IMarketingActionSectionsOf => {
  const sections: Record<MarketingActionSection, IMarketingAction[]> = {
    waste: [],
    grow: [],
    data: [],
  };
  const seen = new Set<string>();
  actions.forEach((action) => {
    const section = marketingActionSectionOf(action);
    const key = `${section}|${actionEntityKey(action)}`;
    if (seen.has(key)) return;
    seen.add(key);
    sections[section].push(action);
  });
  return sections;
};

/* ── «Стоит усилить»: одна карточка на курс ─────────────────────────── */

/** Строк-запросов в карточке курса до «ещё N». */
export const MARKETING_FAMILY_ACTION_LINES = 3;

/** Карточка секции: одно действие или курс со своими запросами. */
export type IMarketingActionItem =
  | { type: 'single'; key: string; action: IMarketingAction }
  | {
      type: 'family';
      key: string;
      familySlug: string;
      /** Действия курса в порядке сервера (первое — самое важное). */
      actions: IMarketingAction[];
      /**
       * Оценка денег курса (F-E16): поле сервера `familyValuePlnEst` — та
       * же, что у карточки курса в SEO › «Что написать» (наибольшая
       * ценность тем курса), фронт её не пересчитывает. Нет поля — НАИБОЛЬШАЯ
       * из запросов, не сумма: «data science» и «data scientist» — одни и
       * те же люди, сумма завышала бы вдвое.
       */
      valuePlnEst: number | null;
      /**
       * Запросов курса, где нас нет или мы ниже 10-го места (F-E16): поле
       * сервера `familyKeywords` (то же число, что у курса в «Что
       * написать»); нет поля — число строк карточки.
       */
      keywordCount: number;
      /** Самая высокая важность среди запросов. */
      severity: string;
    };

/** Действие про поисковый спрос курса — сворачивается в карточку курса. */
export const marketingIsFamilySeoAction = (action: IMarketingAction): boolean =>
  Boolean(action.familySlug) &&
  (action.source === 'seo' || action.kind === 'seo_gap_new');

/**
 * Карточки секции: возможности SEO одного курса (запросы, где нас нет или
 * мы ниже 10-го места, и «новые разрывы» курса) — ОДНОЙ карточкой с главными
 * запросами; остальное — как было. Порядок — по первому появлению (сервер
 * уже ранжировал). Курс с одним действием — обычная карточка.
 */
export const marketingActionItems = (
  actions: ReadonlyArray<IMarketingAction>,
): IMarketingActionItem[] => {
  const items: IMarketingActionItem[] = [];
  const byFamily = new Map<string, IMarketingAction[]>();
  actions.forEach((action) => {
    if (!marketingIsFamilySeoAction(action)) {
      items.push({ type: 'single', key: action.id, action });
      return;
    }
    const slug = action.familySlug as string;
    const group = byFamily.get(slug);
    if (group) {
      group.push(action);
      return;
    }
    const fresh = [action];
    byFamily.set(slug, fresh);
    items.push({
      type: 'family',
      key: `family:${slug}`,
      familySlug: slug,
      actions: fresh,
      valuePlnEst: null,
      keywordCount: 0,
      severity: action.severity,
    });
  });
  return items.map((item) => {
    if (item.type === 'single') return item;
    if (item.actions.length === 1) {
      return {
        type: 'single',
        key: item.actions[0].id,
        action: item.actions[0],
      };
    }
    const values = item.actions
      .map((action) => action.valuePlnEst)
      .filter(
        (value): value is number => typeof value === 'number' && value > 0,
      );
    // F-E16: курс целиком — поля сервера (одинаковые у всех элементов
    // курса): оценка и число запросов карточки курса в «Что написать».
    const courseValue =
      item.actions
        .map((action) => action.familyValuePlnEst)
        .find(
          (value): value is number => typeof value === 'number' && value > 0,
        ) ?? null;
    const courseKeywords =
      item.actions
        .map((action) => action.familyKeywords)
        .find(
          (value): value is number => typeof value === 'number' && value > 0,
        ) ?? null;
    // Важность — самая высокая (меньший ранг: critical 0, info 2).
    const severity = item.actions.reduce(
      (best, action) =>
        (SEVERITY_RANK[action.severity] ?? 3) < (SEVERITY_RANK[best] ?? 3)
          ? action.severity
          : best,
      item.actions[0].severity,
    );
    return {
      ...item,
      valuePlnEst: courseValue ?? (values.length ? Math.max(...values) : null),
      keywordCount: courseKeywords ?? item.actions.length,
      severity,
    };
  });
};

/**
 * Показывать ли секцию «Сегодня» (финальная проверка 0103, F-E20): все её
 * карточки подняты в «Сделать сегодня» — нет (пустая рамка с указателем
 * «выше» только удлиняла экран); пустая сама по себе — да, со своим «всё
 * спокойно»; ответа ещё нет — да (загрузка).
 */
export const marketingTodaySectionShown = (
  items: ReadonlyArray<unknown> | null | undefined,
  moved: number,
): boolean => !(items && items.length === 0 && moved > 0);

/* ── Оценка денег: что именно за число (повторное ревью 0103, A9) ───── */

/**
 * Вид денег оценки карточки. У разных видов это РАЗНЫЕ деньги, и складывать
 * их в одно «на кону» нельзя:
 *  - `lost` — расход без результата (расход без лидов, переплата CPL,
 *    расход без продаж);
 *  - `potential` — возможность SEO: грубая оценка выручки в месяц;
 *  - `pending` — договоры, которые ждут подписи (их сумма);
 *  - `spend` — расход под гипотезой бюджета (обновить креатив, починить
 *    разметку / урезать).
 */
export const MARKETING_ACTION_VALUE_KINDS = [
  'lost',
  'potential',
  'pending',
  'spend',
] as const;

export type MarketingActionValueKind = typeof MARKETING_ACTION_VALUE_KINDS[number];

const isValueKind = (
  value: string | null | undefined,
): value is MarketingActionValueKind =>
  Boolean(value) &&
  (MARKETING_ACTION_VALUE_KINDS as readonly string[]).includes(value as string);

/**
 * Вид денег: поле сервера (`valueKind`, пакет 2); без него (старый ответ,
 * незнакомый код) — то же правило по виду действия, что у сервера: SEO —
 * потенциал, договоры без подписи — ждут, гипотеза бюджета — расход,
 * остальное — впустую.
 */
export const marketingActionValueKind = (
  action: Pick<IMarketingAction, 'kind' | 'source' | 'valueKind'>,
): MarketingActionValueKind => {
  if (isValueKind(action.valueKind)) return action.valueKind;
  if (action.source === 'seo') return 'potential';
  if (action.kind === 'sent_not_signed') return 'pending';
  if (action.kind === 'budget_hypothesis') return 'spend';
  return 'lost';
};

/**
 * За сколько дней оценка: поле сервера (`valueWindowDays`); не спрашивали
 * (`undefined`, старый ответ) — запасное правило гипотез бюджета (урезать —
 * 90 дней, прочие — 7); `null` — окна нет или неизвестно.
 */
export const marketingActionValueWindow = (
  action: Pick<IMarketingAction, 'kind' | 'params' | 'valueWindowDays'>,
): number | null => {
  const days = action.valueWindowDays;
  if (typeof days === 'number') return days > 0 ? days : null;
  if (days === null) return null;
  if (action.kind === 'budget_hypothesis') {
    return action.params?.kind === 'cut' ? 90 : 7;
  }
  return null;
};

export interface IMarketingActionValueLabel {
  /** Ключ подписи оценки (`marketing.today.value*`). */
  key: string;
  /** Окно в днях для подписи «за N дн.»; `null` — без окна. */
  days: number | null;
}

/**
 * Подпись оценки карточки: «потрачено впустую за 7 дн. ≈ 429 PLN»,
 * «потенциал ≈ 9 829 PLN/мес (оценка)», «ждут подписи на ≈ …», «расход за
 * 90 дн. ≈ …». Потенциал SEO — всегда «в месяц» (окно сервера — 30).
 */
export const marketingActionValueLabel = (
  action: Pick<
    IMarketingAction,
    'kind' | 'source' | 'params' | 'valueKind' | 'valueWindowDays'
  >,
): IMarketingActionValueLabel => {
  const kind = marketingActionValueKind(action);
  const days = marketingActionValueWindow(action);
  switch (kind) {
    case 'potential':
      return { key: 'marketing.today.valuePotential', days: null };
    case 'pending':
      return { key: 'marketing.today.valuePending', days: null };
    case 'spend':
      return days
        ? { key: 'marketing.today.valueSpendDays', days }
        : { key: 'marketing.today.valueSpend', days: null };
    case 'lost':
    default:
      return days
        ? { key: 'marketing.today.valueLostDays', days }
        : { key: 'marketing.today.valueLost', days: null };
  }
};

export interface IMarketingTodayVerdict {
  /** Карточек с важностью critical (счётчик, без денег). */
  urgent: number;
  /**
   * Расход впустую: сумма оценок вида `lost` секции «Деньги уходят
   * впустую», одна сущность (кампания, объявление) — один раз, наибольшей
   * оценкой.
   */
  lost: number;
  /**
   * Окно «впустую» в днях, если оно ОДНО у всех вошедших карточек (пакет 2:
   * «За 7 дн. впустую ≈ …»); окна разные или неизвестны — `null`.
   */
  lostWindowDays: number | null;
  /**
   * Потенциал SEO — выручка в месяц (грубая оценка), отдельно от денег:
   * карточки курсов (у курса — наибольшая из запросов) и возможности SEO.
   */
  potential: number;
}

const positive = (value: number | null | undefined): number =>
  typeof value === 'number' && value > 0 ? value : 0;

/** Ключ сущности для «один раз»: инспектор, сущность или само действие. */
const valueEntityKey = (action: IMarketingAction): string =>
  action.inspector ??
  (action.entityId ? `entity:${action.entityId}` : `action:${action.id}`);

/**
 * Вердикт над секциями «Сегодня»: «Срочно: N», «Впустую ≈ X» (за N дн.,
 * если окно у всех одно) и отдельно «потенциал SEO ≈ Y/мес» — деньги
 * впустую и возможная выручка не складываются в одно «на кону» (было
 * 712 + 56 294 = «≈ 57 006»).
 */
export const marketingTodayVerdict = (
  sections: Readonly<
    Record<MarketingActionSection, ReadonlyArray<IMarketingActionItem>>
  >,
): IMarketingTodayVerdict => {
  const items = [...sections.waste, ...sections.grow, ...sections.data];
  const urgent = items.filter(
    (item) =>
      (item.type === 'family' ? item.severity : item.action.severity) ===
      'critical',
  ).length;
  const lostByEntity = new Map<string, number>();
  const windows = new Set<number | null>();
  sections.waste.forEach((item) => {
    if (item.type !== 'single') return;
    const { action } = item;
    if (marketingActionValueKind(action) !== 'lost') return;
    const value = positive(action.valuePlnEst);
    if (value <= 0) return;
    windows.add(marketingActionValueWindow(action));
    const key = valueEntityKey(action);
    lostByEntity.set(key, Math.max(lostByEntity.get(key) ?? 0, value));
  });
  const lost = Array.from(lostByEntity.values()).reduce(
    (sum, value) => sum + value,
    0,
  );
  const [onlyWindow] = Array.from(windows);
  const potential = items.reduce((sum, item) => {
    if (item.type === 'family') return sum + positive(item.valuePlnEst);
    return marketingActionValueKind(item.action) === 'potential'
      ? sum + positive(item.action.valuePlnEst)
      : sum;
  }, 0);
  return {
    urgent,
    lost,
    lostWindowDays: windows.size === 1 ? onlyWindow ?? null : null,
    potential,
  };
};

/**
 * Запасная ссылка действия — адрес сервера (`link`, новые вкладки ERP):
 * только его запрос, путь — раздела (`PATHS.marketing`). Старый адрес
 * (сервер пишет `tab=seo&stab=links` у возможности `link_gap`, пакет 2) —
 * сразу новым местом, с якорем (`#seo-links`), а не через редирект.
 */
export const marketingActionHref = (
  action: Pick<IMarketingAction, 'link'>,
): { pathname: string; search: string; hash?: string } | null => {
  const at = action.link.indexOf('?');
  if (at < 0) return null;
  const query = action.link.slice(at + 1);
  if (!query) return null;
  const legacy = marketingLegacyTabTarget(`?${query}`);
  if (legacy) {
    const sharp = legacy.indexOf('#');
    const path = sharp < 0 ? legacy : legacy.slice(0, sharp);
    const mark = path.indexOf('?');
    return {
      pathname: PATHS.marketing,
      search: mark < 0 ? '' : path.slice(mark),
      ...(sharp < 0 ? {} : { hash: legacy.slice(sharp) }),
    };
  }
  return { pathname: PATHS.marketing, search: `?${query}` };
};

/**
 * Числа «Почему» карточки действия: ключ доказательства сервера → формат.
 * Незнакомые ключи не показываются: число без подписи ничего не объясняет.
 */
export const MARKETING_ACTION_METRIC_FORMATS: Readonly<
  Record<string, MarketingActionNumberFormat>
> = {
  spend_7d: 'money',
  spend: 'money',
  spend_90d: 'money',
  fatigued_spend_7d: 'money',
  cpl_7d: 'money',
  cpl_28d: 'money',
  ratio: 'ratio',
  platform_leads: 'count',
  crm_leads: 'count',
  leads_7d: 'count',
  contracts_sent: 'count',
  contracts_pending: 'count',
  contracts_lost: 'count',
  pending_value_pln: 'money',
  sales: 'count',
  impressions_28d: 'count',
  growth_pct: 'pct',
  // ТЗ 0103 §C: страница стала хуже конвертировать — входы и конверсия
  // входа в заявку (доля) за окно и за окно перед ним, падение в процентах.
  entrances: 'count',
  prev_entrances: 'count',
  prev_crm_leads: 'count',
  conversion: 'share',
  prev_conversion: 'share',
  drop_pct: 'pct',
  // ТЗ 0103 §E6 — SEO: упавшие запросы, ответы AI, бюджет DataForSEO в
  // долларах; возможность — позиция, частотность, клики и показы GSC.
  dropped: 'count',
  keywords: 'count',
  volume: 'count',
  answers: 'count',
  prev_answers: 'count',
  position: 'count',
  gsc_impressions_28d: 'count',
  gsc_clicks_28d: 'count',
  estimate_usd: 'usd',
  spent_usd: 'usd',
  cap_usd: 'usd',
  balance_usd: 'usd',
};

export const MARKETING_ACTION_METRIC_KEYS = Object.keys(
  MARKETING_ACTION_METRIC_FORMATS,
);

/**
 * Формат числа доказательства: `pct` — уже в процентах (`growth_pct: 35`),
 * `share` — доля 0..1 (конверсия страницы).
 */
export type MarketingActionNumberFormat =
  | 'money'
  | 'count'
  | 'ratio'
  | 'pct'
  | 'share'
  | 'usd';

export interface IMarketingActionNumber {
  key: string;
  value: number;
  format: MarketingActionNumberFormat;
}

const actionNumber = (
  metrics: Readonly<Record<string, unknown>>,
  key: string,
): number | null => {
  const raw = metrics[key];
  return typeof raw === 'number' || typeof raw === 'string'
    ? marketingNumber(raw)
    : null;
};

/** Числа доказательств действия в порядке словаря форматов. */
export const marketingActionNumbers = (
  metrics: Readonly<Record<string, unknown>>,
): IMarketingActionNumber[] =>
  MARKETING_ACTION_METRIC_KEYS.map((key) => {
    const value = actionNumber(metrics, key);
    return value === null
      ? null
      : { key, value, format: MARKETING_ACTION_METRIC_FORMATS[key] };
  }).filter((item): item is IMarketingActionNumber => item !== null);

/**
 * Пары «было → сейчас» доказательств — мини-график «Почему»: два столбика
 * одной величины (CPL 28 дней против 7, конверсия страницы прошлого окна
 * против этого). Пара — только когда известны обе стороны.
 */
export const MARKETING_ACTION_PAIRS: ReadonlyArray<{
  id: string;
  before: string;
  now: string;
  /** Свой формат пары, если ключей нет в списке чисел (доли SEO). */
  format?: MarketingActionNumberFormat;
}> = [
  { id: 'conversion', before: 'prev_conversion', now: 'conversion' },
  { id: 'crmLeads', before: 'prev_crm_leads', now: 'crm_leads' },
  { id: 'entrances', before: 'prev_entrances', now: 'entrances' },
  { id: 'cpl', before: 'cpl_28d', now: 'cpl_7d' },
  {
    id: 'impressions',
    before: 'prev_impressions_28d',
    now: 'impressions_28d',
  },
  // ТЗ 0103 §E6: доля видимости SEO и доля ответов AI с упоминанием.
  { id: 'sov', before: 'prev_sov', now: 'sov', format: 'share' },
  { id: 'aiShare', before: 'prev_share', now: 'share', format: 'share' },
];

export const MARKETING_ACTION_PAIR_IDS = MARKETING_ACTION_PAIRS.map(
  (pair) => pair.id,
);

export interface IMarketingActionPair {
  id: string;
  before: number;
  now: number;
  /** Ключи доказательств пары — чтобы не повторять их списком чисел. */
  keys: [string, string];
  format: MarketingActionNumberFormat;
}

export const marketingActionPairs = (
  metrics: Readonly<Record<string, unknown>>,
): IMarketingActionPair[] =>
  MARKETING_ACTION_PAIRS.map((pair) => {
    const before = actionNumber(metrics, pair.before);
    const now = actionNumber(metrics, pair.now);
    if (before === null || now === null) return null;
    return {
      id: pair.id,
      before,
      now,
      keys: [pair.before, pair.now] as [string, string],
      format:
        pair.format ?? MARKETING_ACTION_METRIC_FORMATS[pair.now] ?? 'count',
    };
  }).filter((item): item is IMarketingActionPair => item !== null);

/* ── «За неделю изменилось» ──────────────────────────────────────────── */

/** Кампаний на графике изменений. */
export const MARKETING_MOVERS_LIMIT = 8;

/** Метрики графика изменений: Δ CRM-лидов или Δ продаж. */
export const MARKETING_MOVER_METRICS = ['crmLeads', 'sales'] as const;

export type MarketingMoverMetric = typeof MARKETING_MOVER_METRICS[number];

export interface IMarketingMover {
  key: string;
  label: string;
  diff: number;
  current: number;
  previous: number;
}

/**
 * Кампании с наибольшим изменением к прошлой неделе (по модулю): рост и
 * падение вместе, топ-N; без изменений — не показываются. Строка `none`
 * (лиды и продажи без кампании) — не кампания: у неё нет панели.
 */
export const marketingMovers = (
  rows: ReadonlyArray<IMarketingBreakdownRow>,
  metric: MarketingMoverMetric,
  limit = MARKETING_MOVERS_LIMIT,
): IMarketingMover[] =>
  rows
    .filter((row) => row.key !== MARKETING_NONE_KEY)
    .map((row) => {
      const current = marketingNumber(row.metrics[metric]) ?? 0;
      const previous = row.previous
        ? marketingNumber(row.previous[metric]) ?? 0
        : 0;
      return {
        key: row.key,
        label: row.label,
        diff: current - previous,
        current,
        previous,
      };
    })
    .filter((item) => item.diff !== 0)
    .sort(
      (a, b) =>
        Math.abs(b.diff) - Math.abs(a.diff) || a.label.localeCompare(b.label),
    )
    .slice(0, limit);

/* ── «Сводка недели» ─────────────────────────────────────────────────── */

/**
 * Первая фраза сводки: markdown без заголовков, маркеров и выделения —
 * до первой точки, вопроса или восклицания (не дальше 240 знаков).
 */
export const marketingDigestLead = (
  text: string | null | undefined,
): string | null => {
  if (!text) return null;
  const plain = text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'))
    // Маркер списка или цитаты — не текст; число в начале фразы — текст.
    .map((line) => line.replace(/^(?:[-*>]\s+|\d+[.)]\s+)/, ''))
    .join(' ')
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return null;
  const match = plain.match(/^(.{1,240}?[.!?…])(\s|$)/);
  if (match) return match[1];
  return plain.length > 240 ? `${plain.slice(0, 239)}…` : plain;
};

/* ── Пакет 2 (P1, P2, C4): сначала — что делать ─────────────────────── */

/** Карточек в «Сделать сегодня». */
export const MARKETING_TODAY_TOP = 3;

export interface IMarketingTodayTopItem {
  section: MarketingActionSection;
  item: IMarketingActionItem;
}

export interface IMarketingTodayPlan {
  /** «Сделать сегодня»: первые по рангу сервера карточки всех секций. */
  top: IMarketingTodayTopItem[];
  /** Секции без карточек, поднятых наверх. */
  rest: Readonly<Record<MarketingActionSection, IMarketingActionItem[]>>;
  /** Сколько карточек секции поднято в «Сделать сегодня». */
  moved: Readonly<Record<MarketingActionSection, number>>;
}

/**
 * «Сделать сегодня» (пакет 2, P2): три первые карточки по рангу сервера
 * (важность → приоритет вида → оценка в PLN → свежесть — порядок
 * `marketingActions`) поверх всех секций; карточка курса — по своему
 * первому действию. Поднятые карточки из секций убираются: одна и та же
 * карточка дважды на экране читалась бы как два дела.
 */
export const marketingTodayPlan = (
  sections: Readonly<
    Record<MarketingActionSection, ReadonlyArray<IMarketingActionItem>>
  >,
  actions: ReadonlyArray<IMarketingAction>,
  limit: number = MARKETING_TODAY_TOP,
): IMarketingTodayPlan => {
  const rank = new Map(actions.map((action, index) => [action.id, index]));
  const rankOf = (item: IMarketingActionItem): number => {
    const list = item.type === 'family' ? item.actions : [item.action];
    return Math.min(
      ...list.map((action) => rank.get(action.id) ?? Number.MAX_SAFE_INTEGER),
    );
  };
  const all = MARKETING_ACTION_SECTIONS.flatMap((section) =>
    sections[section].map((item) => ({ section, item, rank: rankOf(item) })),
  ).sort((a, b) => a.rank - b.rank);
  const top = all
    .slice(0, limit)
    .map(({ section, item }) => ({ section, item }));
  const topKeys = new Set(top.map(({ item }) => item.key));
  const rest = {} as Record<MarketingActionSection, IMarketingActionItem[]>;
  const moved = {} as Record<MarketingActionSection, number>;
  MARKETING_ACTION_SECTIONS.forEach((section) => {
    rest[section] = sections[section].filter((item) => !topKeys.has(item.key));
    moved[section] = sections[section].length - rest[section].length;
  });
  return { top, rest, moved };
};

/**
 * Ключ строки «Что сделать» карточки (`marketing.today.todo.*`) — глагол
 * по виду действия: возможность SEO — по своему виду, гипотеза бюджета — по
 * своему (`params.kind`), незнакомое — «Откройте и разберите».
 */
export const marketingActionTodoKey = (
  action: Pick<IMarketingAction, 'kind' | 'source' | 'params'>,
): string => {
  if (action.source === 'seo') {
    return MARKETING_TODAY_SEO_TODO_KINDS.includes(action.kind)
      ? `marketing.today.todo.seo.${action.kind}`
      : 'marketing.today.todo.other';
  }
  if (action.kind === 'budget_hypothesis') {
    const kind = action.params?.kind;
    return typeof kind === 'string' &&
      MARKETING_TODAY_HYPOTHESIS_TODO_KINDS.includes(kind)
      ? `marketing.today.todo.hypothesis.${kind}`
      : 'marketing.today.todo.budget_hypothesis';
  }
  return MARKETING_TODAY_SIGNAL_TODO_KINDS.includes(action.kind)
    ? `marketing.today.todo.${action.kind}`
    : 'marketing.today.todo.other';
};

/** Виды сигналов со своей строкой «Что сделать» (локаль-гейт держит). */
export const MARKETING_TODAY_SIGNAL_TODO_KINDS: ReadonlyArray<string> = [
  'creative_fatigue',
  'spend_no_leads',
  'cpl_spike',
  'tracking_gap',
  'coverage_drop',
  'source_stale',
  'campaign_mapping_gap',
  'demand_unserved',
  'demand_rising',
  'seo_position_drop',
  'budget_hypothesis',
  'offline_retract_hold',
  'sent_not_signed',
  'leads_no_sales',
  'page_conversion_drop',
  'serp_rank_drop',
  'competitor_gain',
  'ai_visibility_drop',
  'seo_gap_new',
  'seo_budget_hold',
];

/** Виды возможностей SEO со своей строкой «Что сделать». */
export const MARKETING_TODAY_SEO_TODO_KINDS: ReadonlyArray<string> = [
  'gap_keyword',
  'missing_family',
  'content_topic',
  'striking_distance',
  'click_decline',
  'ai_absent',
  'aio_not_cited',
  'link_gap',
];

/** Виды гипотез бюджета со своей строкой «Что сделать». */
export const MARKETING_TODAY_HYPOTHESIS_TODO_KINDS: ReadonlyArray<string> = [
  'scale_up',
  'cut',
  'refresh_creative',
  'fix_tracking',
];

/** Все ключи «Что сделать» — для локаль-гейта. */
export const MARKETING_TODAY_TODO_KEYS: ReadonlyArray<string> = [
  ...MARKETING_TODAY_SIGNAL_TODO_KINDS.map(
    (kind) => `marketing.today.todo.${kind}`,
  ),
  ...MARKETING_TODAY_SEO_TODO_KINDS.map(
    (kind) => `marketing.today.todo.seo.${kind}`,
  ),
  ...MARKETING_TODAY_HYPOTHESIS_TODO_KINDS.map(
    (kind) => `marketing.today.todo.hypothesis.${kind}`,
  ),
  'marketing.today.todo.other',
  'marketing.today.todo.family',
];

/**
 * Куда уходит больше всего «впустую»: карточка секции «Деньги уходят
 * впустую» с наибольшей оценкой `valueLost` — её имя (кампания,
 * объявление) и сумма: доля в «впустую» решает «в основном» или «больше
 * всего» (финальная проверка 0103, F-E8). Денег впустую нет или имя
 * неизвестно — `null`.
 */
export const marketingTodayLostLeader = (
  waste: ReadonlyArray<IMarketingActionItem>,
): { name: string; value: number } | null => {
  let best: IMarketingAction | null = null;
  waste.forEach((item) => {
    if (item.type !== 'single') return;
    const { action } = item;
    if (marketingActionValueKind(action) !== 'lost') return;
    if ((action.valuePlnEst ?? 0) <= 0) return;
    if (!best || (action.valuePlnEst ?? 0) > (best.valuePlnEst ?? 0)) {
      best = action;
    }
  });
  const found = best as IMarketingAction | null;
  return found?.entityName
    ? { name: found.entityName, value: found.valuePlnEst ?? 0 }
    : null;
};

/** Имя лидера «впустую» (`marketingTodayLostLeader`); `null` — нет. */
export const marketingTodayLostTop = (
  waste: ReadonlyArray<IMarketingActionItem>,
): string | null => marketingTodayLostLeader(waste)?.name ?? null;

/**
 * «За неделю изменилось» — изменения небольшие (пакет 2, C4): у каждой
 * кампании |Δ| ≤ 1 или у всех вместе меньше 10 событий в обоих окнах —
 * тогда серым и фразой «Изменения небольшие», а не красно-зелёной тревогой.
 */
export const marketingMoversSmall = (
  movers: ReadonlyArray<IMarketingMover>,
): boolean => {
  if (!movers.length) return false;
  const allTiny = movers.every((item) => Math.abs(item.diff) <= 1);
  const current = movers.reduce((sum, item) => sum + item.current, 0);
  const previous = movers.reduce((sum, item) => sum + item.previous, 0);
  return allTiny || Math.max(current, previous) < MARKETING_MOVERS_MIN_BASE;
};

/** База «За неделю изменилось», ниже которой изменения — шум. */
export const MARKETING_MOVERS_MIN_BASE = 10;
